import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Action, appendParams, ListView, ObservableDataLoader } from '@bsquare/angular-common';
import { Exports } from '@bsquare/coke-common';
import { Base } from '@bsquare/shared-types';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, delayWhen, map, switchMap, take } from 'rxjs/operators';
import { createCountLoader, createGetLoader } from './observable-data-loader-config';

const POST_OPTIONS = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

// Default to a 60 second notification interval for export completions
const DEFAULT_NOTIFICATION_INTERVAL = 60 * 1000;

const ROOT_ROUTE = '/api/v1/db/export';

@Injectable({
  providedIn: 'root',
})
export class ExportsService {
  private readonly httpClient: HttpClient;
  private readonly countLoader: ObservableDataLoader<number>;
  private readonly getLoader: ObservableDataLoader<any>;

  public constructor() {
    const httpClient = inject(HttpClient);

    this.httpClient = httpClient;
    this.countLoader = createCountLoader(httpClient);
    this.getLoader = createGetLoader(httpClient);
  }

  public requestExport(request: ListView.ExportRequest | string): Observable<Base.CreateResponse> {
    if (typeof request === 'string') {
      return this.httpClient
        .post<Base.CreateResponse>(request, {}, POST_OPTIONS)
        .pipe(
          delayWhen(() =>
            forkJoin([
              this.getLoader.reloadAll().pipe(take(1)),
              this.countLoader.reloadAll().pipe(take(1)),
            ]),
          ),
        );
    } else {
      return this.httpClient
        .post<Base.CreateResponse>(
          request.url,
          request.parameters ? request.parameters : {},
          POST_OPTIONS,
        )
        .pipe(
          delayWhen(() =>
            forkJoin([
              this.getLoader.reloadAll().pipe(take(1)),
              this.countLoader.reloadAll().pipe(take(1)),
            ]),
          ),
        );
    }
  }

  public getExportStatus(options?: Exports.Request): Observable<Exports.Summary[]> {
    return this.getLoader.load(appendParams(`${ROOT_ROUTE}/status`, options));
  }

  public countExportStatus(options?: Exports.Request): Observable<number> {
    return this.countLoader.load(appendParams(`${ROOT_ROUTE}/status`, options, true));
  }

  public getDownloadName(summary: Exports.Summary): string {
    const updated = new Date(summary.updatedDate);
    return `${summary.name}-${Math.floor(updated.getTime() / 1000)}`;
  }

  public getActions(rowData: Exports.Summary): Action<Exports.Summary>[] {
    return [
      {
        name: rowData.state === Exports.ExportState.Completed ? 'Delete' : 'Cancel',
        start:
          rowData.state === Exports.ExportState.Completed
            ? `Deleting ${this.getDownloadName(rowData)}`
            : `Cancelling ${this.getDownloadName(rowData)}`,
        trigger: () =>
          this.deleteExport(rowData.id).pipe(
            map(() => ({
              type: 'success',
              message: `Export ${
                rowData.state === Exports.ExportState.Completed ? 'deleted' : 'cancelled'
              } successfully`,
            })),
          ),
      },
    ];
  }

  public deleteExport(id: string): Observable<void> {
    return this.httpClient
      .delete<void>(`${ROOT_ROUTE}/${id}`)
      .pipe(
        delayWhen(() =>
          forkJoin([
            this.getLoader.reloadAll().pipe(take(1)),
            this.countLoader.reloadAll().pipe(take(1)),
          ]),
        ),
      );
  }

  public getExportCompletions(
    interval = DEFAULT_NOTIFICATION_INTERVAL,
  ): Observable<Exports.Summary[]> {
    let lastPoll: Date | undefined = undefined;
    return timer(0, interval).pipe(
      switchMap(() => {
        const now = new Date();
        return this.getExportStatus({
          state: Exports.ExportState.Completed,
          updatedDate: { min: lastPoll ? lastPoll : now },
        }).pipe(
          take(1),
          catchError((err) => {
            console.warn(err);
            return of([] as Exports.Summary[]);
          }),
          map((result) => {
            lastPoll = now;
            // Transform names to save caller having to do it
            return result.map((res) => {
              res.name = this.getDownloadName(res);
              return res;
            });
          }),
          delayWhen((result) => {
            if (result.length > 0) {
              return forkJoin([
                this.getLoader.reloadAll().pipe(take(1)),
                this.countLoader.reloadAll().pipe(take(1)),
              ]);
            } else {
              return of(undefined);
            }
          }),
        );
      }),
    );
  }
}
